import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Stack,
  Typography,
} from "@mui/material";
import { PaperTitle } from "Components/Shared/PaperTitle";
import type { ReactNode } from "react";
import { Resources, useResource } from "Translations/Resources";

type Props = {
  legend: string;
  children: ReactNode;
  isExpanded?: boolean;
  onToggle?: () => void;
  onNext?: () => void;
  isComplete?: boolean;
  hideIncompleteIcon?: boolean;
  hasErrors?: boolean;
};

export const FormAccordionSection = ({
  legend,
  children,
  isExpanded,
  onToggle,
  onNext,
  isComplete,
  hideIncompleteIcon,
  hasErrors,
}: Props) => {
  const { t } = useResource();

  const shouldShowIncompleteExplanation =
    isComplete !== undefined &&
    !isComplete &&
    !hideIncompleteIcon &&
    !hasErrors;

  return (
    <Accordion
      expanded={isExpanded}
      onChange={onToggle}
      disableGutters
      elevation={0}
      sx={{
        border: "none",
        boxShadow: "none",
        "&::before": { display: "none" },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{ px: 2, py: 0.5 }}
        slotProps={{ content: { sx: { my: 2.5 } } }}
      >
        <Stack direction="row" alignItems="center" gap={1.5}>
          <PaperTitle component="legend" p={0}>
            {legend}
          </PaperTitle>
          {hasErrors ? (
            <CancelOutlinedIcon color="error" fontSize="small" />
          ) : (
            isComplete !== undefined &&
            (isComplete ? (
              <CheckCircleOutlineIcon color="success" fontSize="small" />
            ) : (
              !hideIncompleteIcon && (
                <ErrorOutlineIcon
                  fontSize="small"
                  sx={{ color: "warning.dark" }}
                />
              )
            ))
          )}
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 3, pb: 3 }}>
        {shouldShowIncompleteExplanation && (
          <Stack direction="row" alignItems="center" gap={1} mb={2}>
            <ErrorOutlineIcon fontSize="small" sx={{ color: "warning.dark" }} />
            <Typography variant="body2" color="warning.dark">
              {t(Resources.Common.IncompleteSection)}
            </Typography>
          </Stack>
        )}
        {children}
        {onNext && (
          <Button
            variant="outlined"
            size="small"
            onClick={onNext}
            sx={{ mt: 2, alignSelf: "flex-end" }}
          >
            {t(Resources.Common.Next)}
          </Button>
        )}
      </AccordionDetails>
    </Accordion>
  );
};
